import type { ClosedDate, OpeningHoursRange } from "../api/types";
import { BUSINESS_TIME_ZONE, businessDayOf } from "./businessDay";

/**
 * G-203 — the weekly pattern as the office app reads and edits it.
 *
 * The server stores one row per opening range (a split day is two rows, a
 * closed weekday has none). The editor wants seven rows, Monday to Sunday,
 * each closed or holding one or two ranges; the list wants one line of text.
 * Both conversions live here, with the English weekday names the office app
 * speaks (G-27: the office stays English).
 */

export const WEEKDAYS: ReadonlyArray<{ weekday: number; short: string; long: string }> = [
  { weekday: 1, short: "Mon", long: "Monday" },
  { weekday: 2, short: "Tue", long: "Tuesday" },
  { weekday: 3, short: "Wed", long: "Wednesday" },
  { weekday: 4, short: "Thu", long: "Thursday" },
  { weekday: 5, short: "Fri", long: "Friday" },
  { weekday: 6, short: "Sat", long: "Saturday" },
  { weekday: 7, short: "Sun", long: "Sunday" },
];

export interface DayRange {
  opensAt: string;
  closesAt: string;
}

/** One editor row: closed, or one or two ranges. */
export interface DayHours {
  weekday: number;
  closed: boolean;
  ranges: DayRange[];
}

export const DEFAULT_RANGE: DayRange = { opensAt: "09:00", closesAt: "20:00" };

/** Seven rows from the stored ranges; a weekday with none is closed. */
export function toDayHours(stored: OpeningHoursRange[]): DayHours[] {
  return WEEKDAYS.map(({ weekday }) => {
    const ranges = stored
      .filter((r) => r.weekday === weekday)
      .sort((a, b) => a.opensAt.localeCompare(b.opensAt))
      .map((r) => ({ opensAt: r.opensAt, closesAt: r.closesAt }));
    return { weekday, closed: ranges.length === 0, ranges };
  });
}

/** The stored shape back from the editor; closed days contribute nothing. */
export function fromDayHours(days: DayHours[]): OpeningHoursRange[] {
  const out: OpeningHoursRange[] = [];
  for (const d of days) {
    if (d.closed) continue;
    for (const r of d.ranges) out.push({ weekday: d.weekday, ...r });
  }
  return out;
}

/**
 * Turn a day's single range into a split day without ever producing an
 * overlap: a range that spans the siesta becomes morning and afternoon
 * (09:00–20:00 → 09:00–14:00 and 17:00–20:00); a range with room after it
 * gains a second range after a one-hour gap; a late range with no room after
 * it is cut in the middle with a one-hour gap; a short late range cannot be
 * split and comes back as it was.
 */
export function splitRange(r: DayRange): DayRange[] {
  const open = toMinutes(r.opensAt);
  const close = toMinutes(r.closesAt);
  const LAST = 23 * 60 + 30;
  if (open < 14 * 60 && close > 17 * 60) {
    return [
      { opensAt: r.opensAt, closesAt: "14:00" },
      { opensAt: "17:00", closesAt: r.closesAt },
    ];
  }
  if (close + 60 + 60 <= LAST) {
    const secondOpen = close + 60;
    return [
      { opensAt: r.opensAt, closesAt: r.closesAt },
      { opensAt: toHhmm(secondOpen), closesAt: toHhmm(Math.min(secondOpen + 180, LAST)) },
    ];
  }
  if (close - open >= 180) {
    const mid = open + Math.floor((close - open) / 2);
    return [
      { opensAt: r.opensAt, closesAt: toHhmm(mid - 30) },
      { opensAt: toHhmm(mid + 30), closesAt: r.closesAt },
    ];
  }
  return [{ opensAt: r.opensAt, closesAt: r.closesAt }];
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** The first thing wrong with the editor's rows, or null when it can be saved. */
export function dayHoursProblem(days: DayHours[]): string | null {
  for (const d of days) {
    if (d.closed) continue;
    const name = WEEKDAYS.find((w) => w.weekday === d.weekday)?.long ?? String(d.weekday);
    if (d.ranges.length === 0) return `${name}: add opening hours or mark it closed.`;
    let lastClose = -1;
    for (const r of d.ranges) {
      if (!HHMM.test(r.opensAt) || !HHMM.test(r.closesAt)) return `${name}: type the hours as HH:mm.`;
      if (r.closesAt <= r.opensAt) return `${name}: closing must be after opening.`;
      if (r.opensAt <= toHhmm(lastClose) && lastClose >= 0) return `${name}: the second range must start after the first ends.`;
      lastClose = toMinutes(r.closesAt);
    }
  }
  return null;
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function toHhmm(min: number): string {
  if (min < 0) return "00:00";
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}

function rangesText(ranges: DayRange[]): string {
  return ranges.map((r) => `${r.opensAt}–${r.closesAt}`).join(" and ");
}

/** Consecutive weekdays with the same hours folded: "Mon–Fri" 09:00–20:00, "Sun" closed. */
export function hoursGroups(stored: OpeningHoursRange[]): Array<{ days: string; text: string; closed: boolean }> {
  const days = toDayHours(stored);
  const groups: Array<{ from: number; to: number; text: string }> = [];
  for (const d of days) {
    const text = d.closed ? "" : rangesText(d.ranges);
    const last = groups[groups.length - 1];
    if (last && last.text === text && last.to === d.weekday - 1) last.to = d.weekday;
    else groups.push({ from: d.weekday, to: d.weekday, text });
  }
  return groups.map((g) => ({
    days: g.from === g.to ? WEEKDAYS[g.from - 1].short : `${WEEKDAYS[g.from - 1].short}–${WEEKDAYS[g.to - 1].short}`,
    text: g.text || "Closed",
    closed: !g.text,
  }));
}

/**
 * One line for the list ("Mon–Fri 09:00–20:00 · Sat 10:00–14:00 · closed Sun").
 * No rows at all reads "Hours not set", which is a different fact from
 * "closed every day".
 */
export function hoursSummary(stored: OpeningHoursRange[]): string {
  if (stored.length === 0) return "Hours not set";
  const groups = hoursGroups(stored);
  const open = groups.filter((g) => !g.closed).map((g) => `${g.days} ${g.text}`);
  const closed = groups.filter((g) => g.closed).map((g) => g.days);
  const parts = [...open];
  if (closed.length > 0) parts.push(`closed ${closed.join(", ")}`);
  return parts.join(" · ");
}

/** The next closed date on or after `today` ("YYYY-MM-DD"), within `withinDays` when given. */
export function nextClosedDate(dates: ClosedDate[], today: string, withinDays?: number): ClosedDate | null {
  const limit = withinDays === undefined ? null : addDays(today, withinDays);
  return (
    dates
      .filter((d) => d.date >= today && (limit === null || d.date <= limit))
      .sort((a, b) => a.date.localeCompare(b.date))[0] ?? null
  );
}

/** "25 Dec" for a "YYYY-MM-DD"; the year only when it is not this year. */
export function closedDateLabel(date: string, today: string): string {
  const [y, m, d] = date.split("-").map(Number);
  const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1];
  return y === Number(today.slice(0, 4)) ? `${d} ${month}` : `${d} ${month} ${y}`;
}

/** What the shop is doing right now, for a pill: one rule, Madrid wall clock. */
export type OpenState =
  | { kind: "not-set" }
  | { kind: "open"; closesAt: string }
  | { kind: "closed"; opensText: string };

const SHORT_TO_ISO: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

function madridClock(now: Date): { weekday: number; minutes: number; ymd: string } {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: BUSINESS_TIME_ZONE,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const get = (t: string) => parts.find((x) => x.type === t)?.value ?? "";
  return {
    weekday: SHORT_TO_ISO[get("weekday")] ?? 1,
    minutes: Number(get("hour")) * 60 + Number(get("minute")),
    ymd: businessDayOf(now).ymd,
  };
}

function addDays(ymd: string, days: number): string {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function openState(
  stored: OpeningHoursRange[],
  closedDates: ClosedDate[],
  now: Date = new Date(),
): OpenState {
  if (stored.length === 0) return { kind: "not-set" };
  const clock = madridClock(now);
  const closed = new Set(closedDates.map((d) => d.date));
  const rangesOn = (weekday: number) =>
    stored.filter((r) => r.weekday === weekday).sort((a, b) => a.opensAt.localeCompare(b.opensAt));

  if (!closed.has(clock.ymd)) {
    for (const r of rangesOn(clock.weekday)) {
      const open = toMinutes(r.opensAt);
      const close = toMinutes(r.closesAt);
      if (clock.minutes >= open && clock.minutes < close) return { kind: "open", closesAt: r.closesAt };
      if (clock.minutes < open) return { kind: "closed", opensText: `opens ${r.opensAt}` };
    }
  }
  for (let ahead = 1; ahead <= 14; ahead += 1) {
    const ymd = addDays(clock.ymd, ahead);
    if (closed.has(ymd)) continue;
    const weekday = ((clock.weekday - 1 + ahead) % 7) + 1;
    const first = rangesOn(weekday)[0];
    if (first) {
      const when = ahead === 1 ? "tomorrow" : WEEKDAYS[weekday - 1].short;
      return { kind: "closed", opensText: `opens ${first.opensAt} ${when}` };
    }
  }
  return { kind: "closed", opensText: "no opening in the next two weeks" };
}

/** The panel's rows: the same groups as the summary, in weekday order. */
export function hoursLines(stored: OpeningHoursRange[]): Array<{ days: string; text: string; closed: boolean }> {
  return stored.length === 0 ? [] : hoursGroups(stored);
}
